import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { noteTypeToDivisionsPerMeasure } from 'Utils';
import TimeSignature from './TimeSignature';
import * as Tone from 'tone';
import ITimeKeeper from './ITimeKeeper';
import { measureDurationSecs } from './MeterTiming';
import logger from 'Utils/Logger';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { awsRum } from 'Utils/AwsRum';

export default class NativeTimeKeeper implements ITimeKeeper {
  public static get METRONOME_SCHEDULE_AHEAD_TIME(): number {
    return 0.2;
  }

  private bpm: number = -1;
  private timeSignatures: TimeSignature[] = [];
  private timeSigFracts: number[] = [];
  private startMeasure: number = 0;
  private countInMeasures: number = 1;
  private tickNoteType: NoteType = NoteType._32nd;
  private paused: boolean = true;
  private elapsedTicksOffset: number = 0;
  private startTime: number = 0;
  private measureDurationSecs: number = 3.333;
  private ticksPerMeasure: number = 0;
  private secondsPerTick: number = 0;

  private cachedCurrentTime: number = performance.now() / 1000;
  private nativeTimeInitialized = false;
  private nativeToPerformanceOffset: number = 0;

  // Diagnostic counters
  private elapsedTicksCallCount = 0;
  private lastLoggedElapsedTicks = -Infinity;
  private audioTimestampCallCount = 0;
  private lastLoggedAudioTimestamp = -Infinity;

  updateCurrentTime(nativeTimeSecs: number): void {
    this.nativeToPerformanceOffset = nativeTimeSecs - performance.now() / 1000;
    this.cachedCurrentTime = nativeTimeSecs;
    this.nativeTimeInitialized = true;
  }

  private getCurrentTimeSecs(): number {
    if (this.nativeTimeInitialized) {
      return performance.now() / 1000 + this.nativeToPerformanceOffset;
    }
    return performance.now() / 1000;
  }

  unpause(bpm: number, countInMeasures: number, startMeasure: number, tickNoteType: NoteType) {
    logger.debug(
      '[NativeTimeKeeper.unpause] ENTRY — paused:',
      this.paused,
      'bpm:',
      bpm,
      'countInMeasures:',
      countInMeasures,
      'startMeasure:',
      startMeasure,
      'tickNoteType:',
      tickNoteType,
      'cachedCurrentTime:',
      this.cachedCurrentTime,
      'nativeTimeInitialized:',
      this.nativeTimeInitialized,
    );
    this.bpm = bpm;
    this.countInMeasures = countInMeasures;
    this.startMeasure = startMeasure;
    this.tickNoteType = tickNoteType;

    const timeSignature = this.timeSignatures[0];
    if (!timeSignature) {
      logger.warn('[NativeTimeKeeper.unpause] NO TIME SIGNATURE — returning early');
      return;
    }

    // Any playable meter: the tempo follows the meter's beat grouping
    // (see MeterTiming: quarters in 4/4, dotted quarters in 6/8), the same rule the native schedulers use. Throws on a meter
    // that is not playable.
    this.measureDurationSecs = measureDurationSecs(this.bpm, timeSignature);

    this.ticksPerMeasure = noteTypeToDivisionsPerMeasure(this.tickNoteType, timeSignature);
    this.secondsPerTick = this.measureDurationSecs / this.ticksPerMeasure;

    // Multiply ticksPerMeasure by the full (possibly fractional) startMeasure,
    // then floor to get an integer tick offset. This matches the web TimeKeeper
    // path (ticksPerMeasure * startMeasure) and the Swift NativeScheduler
    // (Int(Double(ticksPerMeasure) * startMeasure)). For example, with
    // ticksPerMeasure=32 and startMeasure=5.75: 32 * 5.75 = 184.
    this.elapsedTicksOffset = Math.floor(this.ticksPerMeasure * this.startMeasure);

    const metronomeMeasureStartTime = this.getCurrentTimeSecs();
    this.startTime = metronomeMeasureStartTime + this.measureDurationSecs * this.countInMeasures;

    logger.debug(
      '[NativeTimeKeeper.unpause] COMPUTED — startTime:',
      this.startTime,
      'metronomeMeasureStartTime:',
      metronomeMeasureStartTime,
      'measureDurationSecs:',
      this.measureDurationSecs,
      'secondsPerTick:',
      this.secondsPerTick,
      'ticksPerMeasure:',
      this.ticksPerMeasure,
      'elapsedTicksOffset:',
      this.elapsedTicksOffset,
    );

    this.paused = false;
    logger.debug('[NativeTimeKeeper.unpause] paused set to FALSE');
  }

  /**
   * Overwrite startTime with the exact value computed by the native scheduler,
   * guaranteeing JS-side tick math matches native tick dispatch.
   *
   * Also accepts the current native time so that cachedCurrentTime stays
   * consistent with the corrected startTime.  Without this, a MIDI note
   * arriving before the first tick event would compute
   *   audioTimeToMeasureTimestamp() = startMeasure + (stale_cached - corrected_start) / dur
   * which is negative (stale_cached < corrected_start) and misses every
   * event bucket, causing the first note to be graded as a miss.
   */
  setStartTime(nativeStartTime: number, currentNativeTime?: number): void {
    const oldStartTime = this.startTime;
    const oldCachedTime = this.cachedCurrentTime;
    this.startTime = nativeStartTime;
    if (currentNativeTime !== undefined) {
      this.nativeToPerformanceOffset = currentNativeTime - performance.now() / 1000;
      this.cachedCurrentTime = currentNativeTime;
    }
    logger.debug(
      '[NativeTimeKeeper.setStartTime] overwriting startTime:',
      oldStartTime,
      '→',
      nativeStartTime,
      'delta:',
      nativeStartTime - oldStartTime,
      'cachedCurrentTime:',
      oldCachedTime,
      '→',
      this.cachedCurrentTime,
    );
  }

  pause() {
    logger.debug(
      '[NativeTimeKeeper.pause] pausing — startTime:',
      this.startTime,
      'cachedCurrentTime:',
      this.cachedCurrentTime,
      'elapsedTicksOffset:',
      this.elapsedTicksOffset,
      'wasPaused:',
      this.paused,
    );
    this.paused = true;
  }

  isPaused() {
    return this.paused;
  }

  setBpm(newBpm: number) {
    const oldBpm = this.bpm;
    this.bpm = newBpm;

    if (!this.paused) {
      const _timestamp = this.audioTimeToMeasureTimestamp();

      const timeSinceStart = this.getCurrentTimeSecs() - this.startTime;
      const bpmScale = oldBpm / newBpm;
      this.startTime = this.getCurrentTimeSecs() - timeSinceStart * bpmScale;

      const _newTimestamp = this.audioTimeToMeasureTimestamp();

      const ts = this.timeSignatures[0];
      if (!ts) {
        logger.warn('[NativeTimeKeeper.setBpm] no timeSignature — skipping duration recalc');
      } else {
        this.measureDurationSecs = measureDurationSecs(this.bpm, ts);
        this.secondsPerTick = this.measureDurationSecs / this.ticksPerMeasure;
      }
      logger.debug(
        '[NativeTimeKeeper.setBpm] PLAYING: oldBpm=',
        oldBpm,
        'newBpm=',
        newBpm,
        'measureDuration=',
        this.measureDurationSecs,
        'secondsPerTick=',
        this.secondsPerTick,
      );
    } else {
      logger.debug(
        '[NativeTimeKeeper.setBpm] PAUSED: storing bpm=',
        newBpm,
        '(will take effect on unpause)',
      );
    }

    MuseFlowAudio.setBpm({ bpm: newBpm }).catch((err: unknown) => {
      logger.error('[NativeTimeKeeper.setBpm] native setBpm failed:', err);
      awsRum?.recordError(err instanceof Error ? err : new Error(String(err)));
    });
  }

  setTimeSignatures(startMeasure: number, timeSignatures: TimeSignature[] = []) {
    if (timeSignatures.length) {
      let i = 0;
      for (
        let j = startMeasure;
        i < timeSignatures.length && j < this.timeSignatures.length;
        i++, j++
      ) {
        this.timeSignatures[j] = timeSignatures[i];
      }
      for (; i < timeSignatures.length; i++) {
        this.timeSignatures.push(timeSignatures[i]);
      }
      // Keep each fraction beside its own time signature, on both branches, so
      // re-seeding with a different meter never leaves a stale fraction.
      this.timeSigFracts = this.timeSignatures.map(
        (timeSignature) => timeSignature?.numerator / timeSignature?.denominator,
      );
    }
  }

  calculateElapsedTicks(): number {
    const currentTime = this.getCurrentTimeSecs();
    const elapsedTime = currentTime - this.startTime;
    let elapsedTicks = Math.floor(elapsedTime / this.secondsPerTick) + this.elapsedTicksOffset;
    elapsedTicks = elapsedTicks === Infinity ? 0 : elapsedTicks;

    this.elapsedTicksCallCount++;
    // Log every 100th call OR when ticks change significantly (by more than 10)
    if (
      this.elapsedTicksCallCount % 100 === 0 ||
      Math.abs(elapsedTicks - this.lastLoggedElapsedTicks) > 10
    ) {
      logger.debug(
        '[NativeTimeKeeper.calculateElapsedTicks] call#' + this.elapsedTicksCallCount,
        'elapsedTime:',
        elapsedTime.toFixed(4),
        'currentTime:',
        currentTime.toFixed(4),
        'startTime:',
        this.startTime.toFixed(4),
        'secondsPerTick:',
        this.secondsPerTick.toFixed(6),
        'elapsedTicksOffset:',
        this.elapsedTicksOffset,
        'result:',
        elapsedTicks,
        'paused:',
        this.paused,
      );
      this.lastLoggedElapsedTicks = elapsedTicks;
    }
    return elapsedTicks;
  }

  resetTime() {
    logger.debug(
      '[NativeTimeKeeper.resetTime] resetting — old startTime:',
      this.startTime,
      'old elapsedTicksOffset:',
      this.elapsedTicksOffset,
      'old paused:',
      this.paused,
    );
    this.startMeasure = 0;
    this.elapsedTicksOffset = 0;
    this.pause();
    this.startTime = this.getCurrentTimeSecs();
    // Reset diagnostic counters
    this.elapsedTicksCallCount = 0;
    this.lastLoggedElapsedTicks = -Infinity;
    this.audioTimestampCallCount = 0;
    this.lastLoggedAudioTimestamp = -Infinity;
  }

  noteTypeToTicksPerMeasure(noteType: NoteType) {
    const timeSignature = this.timeSignatures[0];
    return noteTypeToDivisionsPerMeasure(noteType, timeSignature);
  }

  measureTimestampToTicks(measureTimestamp: number): number {
    return this.ticksPerMeasure * measureTimestamp;
  }

  measureDurationAt(_index: number) {
    const timeSignature = this.timeSignatures[0];
    if (timeSignature) {
      return timeSignature?.numerator / timeSignature?.denominator;
    } else {
      return 0;
    }
  }

  measureTimestampToAudioTime(measureTimestamp: number): number {
    const measureOffset = measureTimestamp - this.startMeasure;
    return this.startTime + measureOffset * this.measureDurationSecs;
  }

  audioTimeToMeasureTimestamp(): number {
    const currentTime = this.getCurrentTimeSecs();
    const timeOffset = currentTime - this.startTime;
    const timestamp = this.startMeasure + timeOffset / this.measureDurationSecs;
    const result = timestamp * this.measureDurationAt(0);

    this.audioTimestampCallCount++;
    if (
      this.audioTimestampCallCount % 200 === 0 ||
      Math.abs(result - this.lastLoggedAudioTimestamp) > 1.0
    ) {
      logger.debug(
        '[NativeTimeKeeper.audioTimeToMeasureTimestamp] call#' + this.audioTimestampCallCount,
        'timeOffset:',
        timeOffset.toFixed(4),
        'startMeasure:',
        this.startMeasure,
        'measureDurationSecs:',
        this.measureDurationSecs.toFixed(4),
        'measureDurationAt0:',
        this.measureDurationAt(0),
        'result:',
        result.toFixed(4),
        'paused:',
        this.paused,
      );
      this.lastLoggedAudioTimestamp = result;
    }
    return result;
  }

  getPlaybackStartTicks(): number {
    return this.elapsedTicksOffset;
  }

  getPlaybackStartTime(): number {
    return this.startTime;
  }

  getTicksPerMeasure(): number {
    return this.ticksPerMeasure;
  }

  getAudioContext(): Tone.BaseContext {
    return Tone.context;
  }

  getAudioContextTime(): number {
    return this.getCurrentTimeSecs();
  }

  getTimeSignature(): TimeSignature {
    return this.timeSignatures[0];
  }

  getTimeSigFract(): number {
    return this.timeSigFracts[0];
  }

  getTickNoteType(): NoteType {
    return this.tickNoteType;
  }

  getMeasureDurationSecs(): number {
    return this.measureDurationSecs;
  }

  getBpm(): number {
    return this.bpm;
  }

  getIsCountingIn(): boolean {
    return this.getCurrentTimeSecs() < this.startTime;
  }
}
